import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  AdvancedPanel,
  EditorHeader,
  EditorPanel,
  SwitchRow,
  VisibilityPanel,
  fieldProps,
} from "./EditorControls";
import { highlightIconOptions } from "./HighlightIcon";

const socialFields = [
  ["instagram", "Instagram"],
  ["facebook", "Facebook"],
  ["tiktok", "TikTok"],
  ["linkedin", "LinkedIn"],
  ["youtube", "YouTube"],
  ["website", "Sitio externo"],
];

function LinkFields({ label, labelValue, urlValue, onLabelChange, onUrlChange, disabled = false }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
      <TextField {...fieldProps} label={`Texto ${label}`} value={labelValue || ""} onChange={onLabelChange} disabled={disabled} />
      <TextField {...fieldProps} label={`Destino ${label}`} value={urlValue || ""} onChange={onUrlChange} disabled={disabled} />
    </Stack>
  );
}

function CopyFields({ value, onChange, includeCountLabels = false }) {
  return (
    <Stack spacing={1.25}>
      <TextField {...fieldProps} label="Texto superior (opcional)" value={value.eyebrow || ""} onChange={(event) => onChange("eyebrow", event.target.value)} />
      <TextField {...fieldProps} label="Título" value={value.title || ""} onChange={(event) => onChange("title", event.target.value)} />
      <TextField {...fieldProps} label="Subtítulo" value={value.subtitle || ""} onChange={(event) => onChange("subtitle", event.target.value)} multiline minRows={2} />
      <TextField {...fieldProps} label="Texto del botón" value={value.ctaLabel || ""} onChange={(event) => onChange("ctaLabel", event.target.value)} />
      {includeCountLabels ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
          <TextField {...fieldProps} label="Singular" value={value.productSingular || ""} onChange={(event) => onChange("productSingular", event.target.value)} />
          <TextField {...fieldProps} label="Plural" value={value.productPlural || ""} onChange={(event) => onChange("productPlural", event.target.value)} />
        </Stack>
      ) : null}
      <AdvancedPanel title="Mensajes automáticos" description="Solo se muestran durante carga, errores o catálogos vacíos.">
        <Stack spacing={1.25}>
          <TextField {...fieldProps} label="Mientras carga" value={value.loadingText || ""} onChange={(event) => onChange("loadingText", event.target.value)} />
          <TextField {...fieldProps} label="Sin resultados" value={value.emptyText || ""} onChange={(event) => onChange("emptyText", event.target.value)} />
          <TextField {...fieldProps} label="Error" value={value.errorText || ""} onChange={(event) => onChange("errorText", event.target.value)} />
        </Stack>
      </AdvancedPanel>
    </Stack>
  );
}

function NavbarInspector({ form, setGroup, productsEnabled, simpleMode = false }) {
  return (
    <>
      <EditorHeader title="Navbar" description="Configurá solo lo que el visitante necesita ver en la navegación principal." />
      <VisibilityPanel label="Navbar" checked={form.sections.showNavbar !== false} onChange={(event) => setGroup("sections", "showNavbar", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title={simpleMode ? "Accesos visibles" : "Diseño y accesos"}>
          <Stack spacing={1.25}>
            {!simpleMode ? (
              <TextField {...fieldProps} select label="Diseño" value={form.navigation.style || "style-1"} onChange={(event) => setGroup("navigation", "style", event.target.value)}>
                <MenuItem value="style-1">Clásica translúcida</MenuItem>
                <MenuItem value="style-2">Comercial con información superior</MenuItem>
                <MenuItem value="style-3">Oscura con logo centrado</MenuItem>
              </TextField>
            ) : null}
            <SwitchRow label="Productos" helper={!productsEnabled ? "Requiere el módulo Productos" : "Incluye acceso al catálogo y categorías"} checked={productsEnabled && form.navigation.showProductsLink} disabled={!productsEnabled} onChange={(event) => setGroup("navigation", "showProductsLink", event.target.checked)} />
            <Divider />
            <SwitchRow label="Contacto" checked={form.navigation.showContactLink} onChange={(event) => setGroup("navigation", "showContactLink", event.target.checked)} />
            <Divider />
            <SwitchRow label="Carrito" helper={!productsEnabled ? "Requiere el módulo Productos" : "Visible cuando el proyecto admite pedidos"} checked={productsEnabled && form.navigation.showCart} disabled={!productsEnabled} onChange={(event) => setGroup("navigation", "showCart", event.target.checked)} />
            <Divider />
            <SwitchRow label="Acceso de clientes" checked={form.navigation.showCustomerAccess} onChange={(event) => setGroup("navigation", "showCustomerAccess", event.target.checked)} />
            <Divider />
            <SwitchRow label="Redes sociales" checked={form.navigation.showSocialLinks} onChange={(event) => setGroup("navigation", "showSocialLinks", event.target.checked)} />
          </Stack>
        </EditorPanel>

        <EditorPanel title="Textos visibles" description="Los destinos habituales ya vienen configurados.">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Inicio" value={form.navigation.homeLabel || ""} onChange={(event) => setGroup("navigation", "homeLabel", event.target.value)} />
            <TextField {...fieldProps} label="Productos" value={form.navigation.productsLabel || ""} onChange={(event) => setGroup("navigation", "productsLabel", event.target.value)} disabled={!productsEnabled} />
            <TextField {...fieldProps} label="Contacto" value={form.navigation.contactLabel || ""} onChange={(event) => setGroup("navigation", "contactLabel", event.target.value)} />
            <TextField {...fieldProps} label="Acceso" value={form.navigation.loginLabel || ""} onChange={(event) => setGroup("navigation", "loginLabel", event.target.value)} />
          </Stack>
        </EditorPanel>

        <AdvancedPanel title="Destinos y textos del sistema" description="Modificalos únicamente si el proyecto usa rutas personalizadas.">
          <Stack spacing={1.25}>
            <LinkFields label="Inicio" labelValue={form.navigation.homeLabel} urlValue={form.navigation.homeUrl} onLabelChange={(event) => setGroup("navigation", "homeLabel", event.target.value)} onUrlChange={(event) => setGroup("navigation", "homeUrl", event.target.value)} />
            <LinkFields label="Productos" labelValue={form.navigation.productsLabel} urlValue={form.navigation.productsUrl} onLabelChange={(event) => setGroup("navigation", "productsLabel", event.target.value)} onUrlChange={(event) => setGroup("navigation", "productsUrl", event.target.value)} disabled={!productsEnabled} />
            <LinkFields label="Contacto" labelValue={form.navigation.contactLabel} urlValue={form.navigation.contactUrl} onLabelChange={(event) => setGroup("navigation", "contactLabel", event.target.value)} onUrlChange={(event) => setGroup("navigation", "contactUrl", event.target.value)} />
            <LinkFields label="Acceso" labelValue={form.navigation.loginLabel} urlValue={form.navigation.loginUrl} onLabelChange={(event) => setGroup("navigation", "loginLabel", event.target.value)} onUrlChange={(event) => setGroup("navigation", "loginUrl", event.target.value)} />
            <Divider />
            <TextField {...fieldProps} label="Mi cuenta" value={form.navigation.accountLabel || ""} onChange={(event) => setGroup("navigation", "accountLabel", event.target.value)} />
            <TextField {...fieldProps} label="Cerrar sesión" value={form.navigation.logoutLabel || ""} onChange={(event) => setGroup("navigation", "logoutLabel", event.target.value)} />
            <TextField {...fieldProps} label="Ver todos los productos" value={form.navigation.allProductsLabel || ""} onChange={(event) => setGroup("navigation", "allProductsLabel", event.target.value)} />
            <TextField {...fieldProps} label="Tooltip del carrito" value={form.navigation.cartTooltip || ""} onChange={(event) => setGroup("navigation", "cartTooltip", event.target.value)} />
            <TextField {...fieldProps} label="Etiqueta abrir menú" value={form.navigation.menuAriaLabel || ""} onChange={(event) => setGroup("navigation", "menuAriaLabel", event.target.value)} />
            <TextField {...fieldProps} label="Etiqueta cerrar menú" value={form.navigation.closeMenuAriaLabel || ""} onChange={(event) => setGroup("navigation", "closeMenuAriaLabel", event.target.value)} />
            <TextField {...fieldProps} label="Etiqueta abrir carrito" value={form.navigation.cartAriaLabel || ""} onChange={(event) => setGroup("navigation", "cartAriaLabel", event.target.value)} />
          </Stack>
        </AdvancedPanel>
      </Stack>
    </>
  );
}

function HeroInspector({ form, setGroup, addSlide, updateSlide, removeSlide, productsEnabled, simpleMode = false, onOpenAssets }) {
  const slides = Array.isArray(form.hero.slides) ? form.hero.slides : [];
  return (
    <>
      <EditorHeader title="Hero principal" description="Concentrá la primera pantalla en una propuesta clara y uno o dos llamados a la acción." />
      <VisibilityPanel label="Hero" checked={form.sections.showHero} onChange={(event) => setGroup("sections", "showHero", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title="Mensaje principal">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Texto superior (opcional)" value={form.hero.eyebrow || ""} onChange={(event) => setGroup("hero", "eyebrow", event.target.value)} />
            <TextField {...fieldProps} label="Título" value={form.hero.title || ""} onChange={(event) => setGroup("hero", "title", event.target.value)} multiline minRows={2} />
            <TextField {...fieldProps} label="Descripción" value={form.hero.subtitle || ""} onChange={(event) => setGroup("hero", "subtitle", event.target.value)} multiline minRows={3} />
          </Stack>
        </EditorPanel>

        <EditorPanel title="Botones" description="Dejá el texto vacío para ocultar un botón.">
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>PRINCIPAL</Typography>
              <LinkFields label="principal" labelValue={form.hero.primaryCtaLabel} urlValue={form.hero.primaryCtaUrl} onLabelChange={(event) => setGroup("hero", "primaryCtaLabel", event.target.value)} onUrlChange={(event) => setGroup("hero", "primaryCtaUrl", event.target.value)} />
              <SwitchRow dense label="Depende de Productos" helper={!productsEnabled ? "Se ocultará mientras Productos esté desactivado" : "Se oculta automáticamente si el módulo no está disponible"} checked={form.hero.primaryCtaRequiresProducts} onChange={(event) => setGroup("hero", "primaryCtaRequiresProducts", event.target.checked)} />
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>SECUNDARIO</Typography>
              <LinkFields label="secundario" labelValue={form.hero.secondaryCtaLabel} urlValue={form.hero.secondaryCtaUrl} onLabelChange={(event) => setGroup("hero", "secondaryCtaLabel", event.target.value)} onUrlChange={(event) => setGroup("hero", "secondaryCtaUrl", event.target.value)} />
              <SwitchRow dense label="Depende de Productos" checked={form.hero.secondaryCtaRequiresProducts} onChange={(event) => setGroup("hero", "secondaryCtaRequiresProducts", event.target.checked)} />
            </Box>
          </Stack>
        </EditorPanel>

        <AdvancedPanel title={`Carrusel e imagen de respaldo (${slides.length}/6)`} description="Las imágenes se administran como archivos para que el cliente no tenga que trabajar con URLs.">
          {simpleMode ? (
            <Alert
              severity="info"
              action={onOpenAssets ? <Button size="small" onClick={onOpenAssets} sx={{ textTransform: "none", fontWeight: 900 }}>Cargar imágenes</Button> : null}
            >
              La imagen principal, su posición y los slides del carrusel se cargan desde Recursos visuales.
            </Alert>
          ) : (
            <Stack spacing={1.25}>
              <TextField {...fieldProps} label="Imagen de respaldo" value={form.hero.imageUrl || ""} onChange={(event) => setGroup("hero", "imageUrl", event.target.value)} helperText="Se usa cuando el carrusel está vacío." />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 900 }}>Imágenes del carrusel</Typography>
                <Button type="button" size="small" startIcon={<AddRoundedIcon />} onClick={addSlide} disabled={slides.length >= 6} sx={{ textTransform: "none", fontWeight: 900 }}>
                  Agregar
                </Button>
              </Stack>
              {slides.length === 0 ? <Alert severity="info">No hay imágenes cargadas. Se utilizará la imagen de respaldo.</Alert> : null}
              {slides.map((slide, index) => (
                <EditorPanel
                  key={`slide-${index}`}
                  title={`Imagen ${index + 1}`}
                  action={
                    <Button type="button" color="error" size="small" onClick={() => removeSlide(index)} sx={{ minWidth: 0, textTransform: "none" }}>
                      Quitar
                    </Button>
                  }
                  sx={{ boxShadow: "none" }}
                >
                  <Stack spacing={1.1}>
                    <TextField {...fieldProps} label="URL" value={slide.url || ""} onChange={(event) => updateSlide(index, "url", event.target.value)} />
                    <TextField {...fieldProps} label="Texto alternativo" value={slide.alt || ""} onChange={(event) => updateSlide(index, "alt", event.target.value)} />
                    <TextField {...fieldProps} select label="Posición" value={slide.position || "center"} onChange={(event) => updateSlide(index, "position", event.target.value)}>
                      <MenuItem value="center">Centro</MenuItem>
                      <MenuItem value="top">Arriba</MenuItem>
                      <MenuItem value="bottom">Abajo</MenuItem>
                      <MenuItem value="left">Izquierda</MenuItem>
                      <MenuItem value="right">Derecha</MenuItem>
                    </TextField>
                  </Stack>
                </EditorPanel>
              ))}
            </Stack>
          )}
        </AdvancedPanel>
      </Stack>
    </>
  );
}

function CatalogOverviewInspector({ form, setGroup, productsEnabled }) {
  if (!productsEnabled) {
    return (
      <>
        <EditorHeader title="Catálogo" description="La landing está preparada para reutilizar productos, marcas y categorías del proyecto." />
        <Alert severity="warning">El módulo Productos está desactivado. Activándolo en el proyecto se habilitarán estos bloques.</Alert>
      </>
    );
  }

  const catalogSections = [
    ["showProducts", "Bloque principal"],
    ["showFeatured", "Productos destacados"],
    ["showBrands", "Marcas"],
    ["showCategories", "Categorías"],
    ["showSpotlight", "Spotlight"],
  ];

  return (
    <>
      <EditorHeader title="Catálogo" description="Definí si la web ofrece productos, servicios o ambos. La misma publicación se reutiliza sin duplicar modelos ni cargas." />
      <EditorPanel title="Tipo de catálogo">
        <TextField
          {...fieldProps}
          select
          fullWidth
          label="Qué ofrece este negocio"
          value={form.catalog?.mode || "auto"}
          onChange={(event) => setGroup("catalog", "mode", event.target.value)}
          helperText="Automático detecta el tipo según las publicaciones activas. Servicios usa una página sin carrito ni filtros de tienda."
        >
          <MenuItem value="auto">Automático según publicaciones</MenuItem>
          <MenuItem value="products">Solo productos</MenuItem>
          <MenuItem value="services">Solo servicios</MenuItem>
          <MenuItem value="mixed">Productos y servicios</MenuItem>
        </TextField>
      </EditorPanel>
      <EditorPanel title="Bloques visibles">
        <Stack divider={<Divider flexItem />}>
          {catalogSections.map(([field, label]) => (
            <SwitchRow key={field} label={label} checked={form.sections[field]} onChange={(event) => setGroup("sections", field, event.target.checked)} />
          ))}
        </Stack>
      </EditorPanel>
    </>
  );
}

function FeaturedInspector({ form, setGroup, setNested, productsEnabled }) {
  if (!productsEnabled) return <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />;
  return (
    <>
      <EditorHeader title="Productos destacados" description="Presentá una selección automática del catálogo sin duplicar la carga de productos." />
      <VisibilityPanel label="Productos destacados" checked={form.sections.showFeatured} onChange={(event) => setGroup("sections", "showFeatured", event.target.checked)} />
      <CopyFields value={form.catalog.featured} onChange={(field, value) => setNested("catalog", "featured", field, value)} />
    </>
  );
}

function BrandsInspector({ form, setGroup, setNested, productsEnabled, onOpenAssets }) {
  if (!productsEnabled) return <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />;
  return (
    <>
      <EditorHeader title="Marcas" description="Mostrá las marcas disponibles y dirigí al visitante al catálogo filtrado." />
      <VisibilityPanel label="Marcas" checked={form.sections.showBrands} onChange={(event) => setGroup("sections", "showBrands", event.target.checked)} />
      <CopyFields value={form.catalog.brands} onChange={(field, value) => setNested("catalog", "brands", field, value)} />
      <Alert
        severity="info"
        sx={{ mt: 1.5 }}
        action={onOpenAssets ? <Button size="small" onClick={onOpenAssets} sx={{ textTransform: "none", fontWeight: 900 }}>Abrir recursos</Button> : null}
      >
        Los logos personalizados se administran desde Recursos visuales.
      </Alert>
    </>
  );
}

function CategoriesInspector({ form, setGroup, setNested, productsEnabled }) {
  if (!productsEnabled) return <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />;
  return (
    <>
      <EditorHeader title="Categorías" description="Ayudá a encontrar productos rápidamente usando las categorías reales del catálogo." />
      <VisibilityPanel label="Categorías" checked={form.sections.showCategories} onChange={(event) => setGroup("sections", "showCategories", event.target.checked)} />
      <CopyFields value={form.catalog.categories} onChange={(field, value) => setNested("catalog", "categories", field, value)} includeCountLabels />
    </>
  );
}

function SpotlightInspector({ form, setGroup, setNested, productsEnabled, simpleMode = false, onOpenAssets }) {
  const value = form.catalog.spotlight;
  if (!productsEnabled) return <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />;
  return (
    <>
      <EditorHeader title="Spotlight" description="Usalo para destacar una campaña, una marca o una categoría puntual." badge="Opcional" />
      <VisibilityPanel label="Spotlight" checked={form.sections.showSpotlight && value.enabled === true} onChange={(event) => {
        setGroup("sections", "showSpotlight", event.target.checked);
        setNested("catalog", "spotlight", "enabled", event.target.checked);
      }} />
      <Stack spacing={1.5}>
        <EditorPanel title="Contenido">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Texto superior" value={value.eyebrow || ""} onChange={(event) => setNested("catalog", "spotlight", "eyebrow", event.target.value)} />
            <TextField {...fieldProps} label="Título" value={value.title || ""} onChange={(event) => setNested("catalog", "spotlight", "title", event.target.value)} />
            <TextField {...fieldProps} label="Descripción" value={value.description || ""} onChange={(event) => setNested("catalog", "spotlight", "description", event.target.value)} multiline minRows={3} />
            <LinkFields label="del botón" labelValue={value.ctaLabel} urlValue={value.ctaUrl} onLabelChange={(event) => setNested("catalog", "spotlight", "ctaLabel", event.target.value)} onUrlChange={(event) => setNested("catalog", "spotlight", "ctaUrl", event.target.value)} />
            <SwitchRow label="El botón depende de Productos" checked={value.ctaRequiresProducts} onChange={(event) => setNested("catalog", "spotlight", "ctaRequiresProducts", event.target.checked)} />
          </Stack>
        </EditorPanel>
        <AdvancedPanel title="Imágenes y colores">
          <Stack spacing={1.25}>
            {simpleMode ? (
              <Alert
                severity="info"
                action={onOpenAssets ? <Button size="small" onClick={onOpenAssets} sx={{ textTransform: "none", fontWeight: 900 }}>Cargar archivos</Button> : null}
              >
                El logo y la imagen de fondo se cargan desde Recursos visuales.
              </Alert>
            ) : (
              <>
                <TextField {...fieldProps} label="Logo URL" value={value.logoUrl || ""} onChange={(event) => setNested("catalog", "spotlight", "logoUrl", event.target.value)} />
                <TextField {...fieldProps} label="Texto alternativo del logo" value={value.logoAlt || ""} onChange={(event) => setNested("catalog", "spotlight", "logoAlt", event.target.value)} />
                <TextField {...fieldProps} label="Imagen de fondo URL" value={value.backgroundImageUrl || ""} onChange={(event) => setNested("catalog", "spotlight", "backgroundImageUrl", event.target.value)} />
              </>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <TextField {...fieldProps} type="color" label="Color de botones y detalles" value={value.primaryColor || "#F35A3A"} onChange={(event) => setNested("catalog", "spotlight", "primaryColor", event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField {...fieldProps} type="color" label="Color de fondo y títulos" value={value.secondaryColor || "#19242F"} onChange={(event) => setNested("catalog", "spotlight", "secondaryColor", event.target.value)} InputLabelProps={{ shrink: true }} />
            </Stack>
          </Stack>
        </AdvancedPanel>
      </Stack>
    </>
  );
}

function AboutInspector({ form, setGroup, productsEnabled, simpleMode = false, onOpenAssets }) {
  return (
    <>
      <EditorHeader title="Nosotros" description="Contá qué hace diferente al negocio sin convertir la sección en un formulario interminable." />
      <VisibilityPanel label="Nosotros" checked={form.sections.showAbout} onChange={(event) => setGroup("sections", "showAbout", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title="Contenido">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Texto superior" value={form.about.eyebrow || ""} onChange={(event) => setGroup("about", "eyebrow", event.target.value)} />
            <TextField {...fieldProps} label="Título" value={form.about.title || ""} onChange={(event) => setGroup("about", "title", event.target.value)} />
            <TextField {...fieldProps} label="Descripción" value={form.about.description || ""} onChange={(event) => setGroup("about", "description", event.target.value)} multiline minRows={4} />
            <LinkFields label="del botón" labelValue={form.about.ctaLabel} urlValue={form.about.ctaUrl} onLabelChange={(event) => setGroup("about", "ctaLabel", event.target.value)} onUrlChange={(event) => setGroup("about", "ctaUrl", event.target.value)} />
            <SwitchRow dense label="El botón depende de Productos" helper={!productsEnabled ? "Productos está desactivado" : "Se oculta si el módulo no está disponible"} checked={form.about.ctaRequiresProducts} onChange={(event) => setGroup("about", "ctaRequiresProducts", event.target.checked)} />
          </Stack>
        </EditorPanel>
        <AdvancedPanel title="Imagen y accesibilidad">
          <Stack spacing={1.25}>
            {simpleMode ? (
              <Alert
                severity="info"
                action={onOpenAssets ? <Button size="small" onClick={onOpenAssets} sx={{ textTransform: "none", fontWeight: 900 }}>Cargar imagen</Button> : null}
              >
                La foto institucional se carga desde Recursos visuales.
              </Alert>
            ) : (
              <TextField {...fieldProps} label="Imagen URL" value={form.about.imageUrl || ""} onChange={(event) => setGroup("about", "imageUrl", event.target.value)} />
            )}
            <TextField {...fieldProps} label="Texto alternativo" value={form.about.imageAlt || ""} onChange={(event) => setGroup("about", "imageAlt", event.target.value)} helperText="Describí brevemente la imagen para accesibilidad." />
          </Stack>
        </AdvancedPanel>
      </Stack>
    </>
  );
}

function HighlightsInspector({ form, setGroup, addHighlight, updateHighlight, removeHighlight, simpleMode = false, onOpenAssets }) {
  const items = Array.isArray(form.highlights) ? form.highlights : [];
  return (
    <>
      <EditorHeader title="Diferenciales" description="Usá pocas tarjetas y mensajes concretos para que el visitante pueda escanearlos rápido." />
      <VisibilityPanel label="Diferenciales" checked={form.sections.showHighlights} onChange={(event) => setGroup("sections", "showHighlights", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title="Encabezado">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Texto superior" value={form.highlightsIntro.eyebrow || ""} onChange={(event) => setGroup("highlightsIntro", "eyebrow", event.target.value)} />
            <TextField {...fieldProps} label="Título" value={form.highlightsIntro.title || ""} onChange={(event) => setGroup("highlightsIntro", "title", event.target.value)} />
            <TextField {...fieldProps} label="Subtítulo" value={form.highlightsIntro.subtitle || ""} onChange={(event) => setGroup("highlightsIntro", "subtitle", event.target.value)} multiline minRows={2} />
          </Stack>
        </EditorPanel>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ fontWeight: 950 }}>Tarjetas</Typography>
            <Typography variant="caption" color="text.secondary">Recomendado: entre 3 y 4.</Typography>
          </Box>
          <Chip size="small" label={`${items.length}/8`} />
        </Stack>

        {items.map((item, index) => (
          <EditorPanel
            key={`highlight-${index}`}
            title={item.title || `Diferencial ${index + 1}`}
            action={
              <Button type="button" color="error" size="small" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => removeHighlight(index)} sx={{ textTransform: "none" }}>
                Quitar
              </Button>
            }
            sx={{ boxShadow: "none" }}
          >
            <Stack spacing={1.15}>
              <TextField {...fieldProps} select label="Ícono identificador" value={item.icon || "star"} onChange={(event) => updateHighlight(index, "icon", event.target.value)} helperText="Elegí el símbolo que representa este beneficio.">
                {highlightIconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField {...fieldProps} label="Título" value={item.title || ""} onChange={(event) => updateHighlight(index, "title", event.target.value)} />
              <TextField {...fieldProps} label="Descripción" value={item.description || ""} onChange={(event) => updateHighlight(index, "description", event.target.value)} multiline minRows={2} />
              {simpleMode ? (
                <TextField {...fieldProps} label="Texto alternativo de la imagen" value={item.imageAlt || ""} onChange={(event) => updateHighlight(index, "imageAlt", event.target.value)} helperText="La imagen se carga desde Recursos visuales." />
              ) : (
                <TextField {...fieldProps} label="Imagen URL (opcional)" value={item.imageUrl || ""} onChange={(event) => updateHighlight(index, "imageUrl", event.target.value)} />
              )}
            </Stack>
          </EditorPanel>
        ))}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <Button type="button" startIcon={<AddRoundedIcon />} onClick={addHighlight} disabled={items.length >= 8} variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
            Agregar diferencial
          </Button>
          {simpleMode && onOpenAssets ? (
            <Button type="button" onClick={onOpenAssets} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}>
              Cargar imágenes
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </>
  );
}

function ContactInspector({ form, setGroup, setNested, simpleMode = false, onOpenContactSettings }) {
  return (
    <>
      <EditorHeader
        title="Contacto"
        description={simpleMode
          ? "Definí el mensaje y los botones de esta sección. Los datos públicos se administran una sola vez."
          : "Centralizá aquí los datos públicos reutilizados por la navbar, la sección de contacto y el footer."}
      />
      <VisibilityPanel label="Contacto" checked={form.sections.showContact} onChange={(event) => setGroup("sections", "showContact", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title="Contenido de la sección">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Título" value={form.contact.title || ""} onChange={(event) => setGroup("contact", "title", event.target.value)} />
            <TextField {...fieldProps} label="Subtítulo" value={form.contact.subtitle || ""} onChange={(event) => setGroup("contact", "subtitle", event.target.value)} multiline minRows={3} />
            <LinkFields label="de contacto" labelValue={form.contact.detailsCtaLabel} urlValue={form.contact.detailsCtaUrl} onLabelChange={(event) => setGroup("contact", "detailsCtaLabel", event.target.value)} onUrlChange={(event) => setGroup("contact", "detailsCtaUrl", event.target.value)} />
          </Stack>
        </EditorPanel>

        {simpleMode ? (
          <Alert
            severity="info"
            action={onOpenContactSettings ? <Button size="small" onClick={onOpenContactSettings} sx={{ textTransform: "none", fontWeight: 900 }}>Editar datos</Button> : null}
          >
            WhatsApp, teléfono, email, dirección, horarios y redes se cargan una sola vez desde Datos y contacto.
          </Alert>
        ) : (
          <EditorPanel title="Datos públicos" description="Se reutilizan automáticamente en otras secciones.">
            <Stack spacing={1.25}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <TextField {...fieldProps} label="WhatsApp" value={form.business.whatsapp || ""} onChange={(event) => setGroup("business", "whatsapp", event.target.value)} helperText="Con código de país" />
                <TextField {...fieldProps} label="Teléfono" value={form.business.phone || ""} onChange={(event) => setGroup("business", "phone", event.target.value)} />
              </Stack>
              <TextField {...fieldProps} label="Email" value={form.business.email || ""} onChange={(event) => setGroup("business", "email", event.target.value)} />
              <TextField {...fieldProps} label="Dirección" value={form.business.address || ""} onChange={(event) => setGroup("business", "address", event.target.value)} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <TextField {...fieldProps} label="Ciudad" value={form.business.city || ""} onChange={(event) => setGroup("business", "city", event.target.value)} />
                <TextField {...fieldProps} label="Provincia" value={form.business.province || ""} onChange={(event) => setGroup("business", "province", event.target.value)} />
              </Stack>
              <TextField {...fieldProps} label="Horarios" value={form.business.schedule || ""} onChange={(event) => setGroup("business", "schedule", event.target.value)} multiline minRows={2} />
            </Stack>
          </EditorPanel>
        )}

        <AdvancedPanel title="WhatsApp, mapa y fondo">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Texto del botón de WhatsApp" value={form.contact.whatsappCtaLabel || ""} onChange={(event) => setGroup("contact", "whatsappCtaLabel", event.target.value)} />
            <TextField {...fieldProps} label="Mensaje inicial" value={form.contact.whatsappMessage || ""} onChange={(event) => setGroup("contact", "whatsappMessage", event.target.value)} multiline minRows={2} />
            {simpleMode ? (
              <Alert severity="info">La imagen de fondo se carga desde Recursos visuales y el mapa desde Datos y contacto.</Alert>
            ) : (
              <>
                <TextField {...fieldProps} label="Imagen de fondo URL" value={form.contact.backgroundImageUrl || ""} onChange={(event) => setGroup("contact", "backgroundImageUrl", event.target.value)} />
                <TextField {...fieldProps} label="Google Maps Embed URL" value={form.business.mapEmbedUrl || ""} onChange={(event) => setGroup("business", "mapEmbedUrl", event.target.value)} multiline minRows={2} />
                <TextField {...fieldProps} label="País" value={form.business.country || ""} onChange={(event) => setGroup("business", "country", event.target.value)} />
              </>
            )}
            <TextField {...fieldProps} label="Título del mapa" value={form.contact.mapTitle || ""} onChange={(event) => setGroup("contact", "mapTitle", event.target.value)} />
          </Stack>
        </AdvancedPanel>

        {!simpleMode ? (
          <AdvancedPanel title="Redes sociales" description="Se usan en la navbar y el footer sin volver a cargarlas.">
            <Stack spacing={1.25}>
              {socialFields.map(([key, label]) => (
                <TextField {...fieldProps} key={key} label={label} value={form.business.socials[key] || ""} onChange={(event) => setNested("business", "socials", key, event.target.value)} />
              ))}
            </Stack>
          </AdvancedPanel>
        ) : null}
      </Stack>
    </>
  );
}

function FloatingWhatsappInspector({ form, setGroup }) {
  return (
    <>
      <EditorHeader title="WhatsApp flotante" description="Controlá el acceso rápido sin repetir todos los datos de contacto." />
      <VisibilityPanel
        label="WhatsApp flotante"
        checked={form.sections.showFloatingWhatsapp !== false}
        onChange={(event) => setGroup("sections", "showFloatingWhatsapp", event.target.checked)}
        helper={!form.business.whatsapp ? "Agregá un número de WhatsApp en la sección Contacto" : undefined}
      />
      <Stack spacing={1.5}>
        <EditorPanel title="Mensaje">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Mensaje inicial" value={form.contact.whatsappMessage || ""} onChange={(event) => setGroup("contact", "whatsappMessage", event.target.value)} multiline minRows={3} />
            <TextField {...fieldProps} label="Número utilizado" value={form.business.whatsapp || ""} onChange={(event) => setGroup("business", "whatsapp", event.target.value)} helperText="Incluí el código de país." />
          </Stack>
        </EditorPanel>
        <AdvancedPanel title="Accesibilidad">
          <TextField {...fieldProps} label="Etiqueta del botón" value={form.contact.floatingWhatsappAriaLabel || ""} onChange={(event) => setGroup("contact", "floatingWhatsappAriaLabel", event.target.value)} />
        </AdvancedPanel>
      </Stack>
    </>
  );
}

function FooterInspector({ form, setGroup, simpleMode = false }) {
  const syncNavigationLabels = () => {
    setGroup("footer", "homeLabel", form.navigation.homeLabel);
    setGroup("footer", "productsLabel", form.navigation.productsLabel);
    setGroup("footer", "contactLabel", form.navigation.contactLabel);
  };

  return (
    <>
      <EditorHeader title="Footer" description="Mantené un cierre simple y reutilizá los datos ya cargados en Identidad y Contacto." />
      <VisibilityPanel label="Footer" checked={form.sections.showFooter !== false} onChange={(event) => setGroup("sections", "showFooter", event.target.checked)} />
      <Stack spacing={1.5}>
        <EditorPanel title={simpleMode ? "Marca y texto" : "Diseño y marca"}>
          <Stack spacing={1.25}>
            {!simpleMode ? (
              <>
                <TextField {...fieldProps} select label="Diseño" value={form.footer.style || "style-1"} onChange={(event) => setGroup("footer", "style", event.target.value)}>
                  <MenuItem value="style-1">Completo oscuro</MenuItem>
                  <MenuItem value="style-2">Comercial claro</MenuItem>
                  <MenuItem value="style-3">Minimalista centrado</MenuItem>
                </TextField>
                <SwitchRow label="Mostrar logo" checked={form.footer.showLogo !== false} onChange={(event) => setGroup("footer", "showLogo", event.target.checked)} />
              </>
            ) : null}
            <TextField {...fieldProps} label="Descripción breve" value={form.footer.tagline || ""} onChange={(event) => setGroup("footer", "tagline", event.target.value)} multiline minRows={3} />
            <TextField {...fieldProps} label="Copyright" value={form.footer.copyright || ""} onChange={(event) => setGroup("footer", "copyright", event.target.value)} helperText="Podés usar {businessName}." />
          </Stack>
        </EditorPanel>

        <AdvancedPanel title="Textos de columnas y navegación" description="Por defecto pueden coincidir con la navbar.">
          <Stack spacing={1.25}>
            <Button startIcon={<ContentCopyRoundedIcon />} onClick={syncNavigationLabels} variant="outlined" size="small" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}>
              Usar textos de la navbar
            </Button>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <TextField {...fieldProps} label="Título Navegación" value={form.footer.navigationTitle || ""} onChange={(event) => setGroup("footer", "navigationTitle", event.target.value)} />
              <TextField {...fieldProps} label="Título Contacto" value={form.footer.contactTitle || ""} onChange={(event) => setGroup("footer", "contactTitle", event.target.value)} />
            </Stack>
            <TextField {...fieldProps} label="Inicio" value={form.footer.homeLabel || ""} onChange={(event) => setGroup("footer", "homeLabel", event.target.value)} />
            <TextField {...fieldProps} label="Productos" value={form.footer.productsLabel || ""} onChange={(event) => setGroup("footer", "productsLabel", event.target.value)} />
            <TextField {...fieldProps} label="Contacto" value={form.footer.contactLabel || ""} onChange={(event) => setGroup("footer", "contactLabel", event.target.value)} />
          </Stack>
        </AdvancedPanel>
        <Alert severity="info">Los datos de contacto y redes se reutilizan desde la sección Contacto.</Alert>
      </Stack>
    </>
  );
}

function IdentityInspector({ form, setRoot, setGroup, onOpenAssets, simpleMode = false }) {
  return (
    <>
      <EditorHeader title="Identidad" description="Definí una vez los datos y colores que se reutilizan en toda la landing." />
      <Stack spacing={1.5}>
        <EditorPanel title="Datos del negocio">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Nombre público" value={form.business.name || ""} onChange={(event) => setGroup("business", "name", event.target.value)} />
            <TextField {...fieldProps} label="Descripción corta" value={form.business.shortDescription || ""} onChange={(event) => setGroup("business", "shortDescription", event.target.value)} multiline minRows={3} />
            {!simpleMode ? (
              <TextField {...fieldProps} select label="Estado público" value={form.publicStatus || "draft"} onChange={(event) => setRoot("publicStatus", event.target.value)}>
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
                <MenuItem value="maintenance">Mantenimiento</MenuItem>
              </TextField>
            ) : null}
          </Stack>
        </EditorPanel>

        {!simpleMode ? (
          <EditorPanel title="Estilo base">
            <Stack spacing={1.25}>
              <TextField {...fieldProps} select label="Plantilla" value={form.theme.template || "catalog-basic"} onChange={(event) => setGroup("theme", "template", event.target.value)}>
                <MenuItem value="catalog-basic">Clásica</MenuItem>
                <MenuItem value="catalog-commercial">Comercial</MenuItem>
                <MenuItem value="catalog-minimal">Minimalista</MenuItem>
              </TextField>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <TextField {...fieldProps} type="color" label="Principal" value={form.theme.primaryColor || "#F35A3A"} onChange={(event) => setGroup("theme", "primaryColor", event.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField {...fieldProps} type="color" label="Secundario" value={form.theme.secondaryColor || "#19242F"} onChange={(event) => setGroup("theme", "secondaryColor", event.target.value)} InputLabelProps={{ shrink: true }} />
              </Stack>
            </Stack>
          </EditorPanel>
        ) : null}
        <Alert
          severity="info"
          action={onOpenAssets ? <Button size="small" onClick={onOpenAssets} sx={{ textTransform: "none", fontWeight: 900 }}>Abrir recursos</Button> : null}
        >
          Logos, favicon e imágenes se administran desde Recursos visuales. La paleta se edita en Colores y estilo.
        </Alert>
      </Stack>
    </>
  );
}

function SeoInspector({ form, setGroup }) {
  const messageGroups = [
    ["Mantenimiento", "maintenanceTitle", "maintenanceDescription"],
    ["Landing deshabilitada", "unavailableTitle", "unavailableDescription"],
    ["Catálogo deshabilitado", "catalogDisabledTitle", "catalogDisabledDescription"],
    ["Pedidos deshabilitados", "ordersDisabledTitle", "ordersDisabledDescription"],
    ["Pago online deshabilitado", "ecommerceDisabledTitle", "ecommerceDisabledDescription"],
  ];

  return (
    <>
      <EditorHeader title="SEO y mensajes del sistema" description="Priorizá los metadatos básicos. Los mensajes excepcionales quedan agrupados para no ocupar espacio diario." />
      <Stack spacing={1.5}>
        <EditorPanel title="SEO básico">
          <Stack spacing={1.25}>
            <TextField {...fieldProps} label="Título SEO" value={form.seo.title || ""} onChange={(event) => setGroup("seo", "title", event.target.value)} />
            <TextField {...fieldProps} label="Descripción SEO" value={form.seo.description || ""} onChange={(event) => setGroup("seo", "description", event.target.value)} multiline minRows={3} />
            <TextField {...fieldProps} label="Imagen para compartir" value={form.seo.imageUrl || ""} onChange={(event) => setGroup("seo", "imageUrl", event.target.value)} />
          </Stack>
        </EditorPanel>
        {messageGroups.map(([title, titleField, descriptionField]) => (
          <AdvancedPanel key={titleField} title={title}>
            <Stack spacing={1.25}>
              <TextField {...fieldProps} label="Título" value={form.messages[titleField] || ""} onChange={(event) => setGroup("messages", titleField, event.target.value)} />
              <TextField {...fieldProps} label="Descripción" value={form.messages[descriptionField] || ""} onChange={(event) => setGroup("messages", descriptionField, event.target.value)} multiline minRows={2} helperText={titleField === "maintenanceTitle" ? "Podés usar {businessName}." : undefined} />
            </Stack>
          </AdvancedPanel>
        ))}
      </Stack>
    </>
  );
}

export default function LandingInspector({
  activeSection,
  form,
  productsEnabled,
  setRoot,
  setGroup,
  setNested,
  addSlide,
  updateSlide,
  removeSlide,
  addHighlight,
  updateHighlight,
  removeHighlight,
  simpleMode = false,
  onOpenAssets,
  onOpenContactSettings,
}) {
  const content = {
    navbar: <NavbarInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} simpleMode={simpleMode} />,
    hero: <HeroInspector form={form} setGroup={setGroup} addSlide={addSlide} updateSlide={updateSlide} removeSlide={removeSlide} productsEnabled={productsEnabled} simpleMode={simpleMode} onOpenAssets={onOpenAssets} />,
    catalog: <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />,
    products: <CatalogOverviewInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} />,
    spotlight: <SpotlightInspector form={form} setGroup={setGroup} setNested={setNested} productsEnabled={productsEnabled} simpleMode={simpleMode} onOpenAssets={onOpenAssets} />,
    featured: <FeaturedInspector form={form} setGroup={setGroup} setNested={setNested} productsEnabled={productsEnabled} />,
    brands: <BrandsInspector form={form} setGroup={setGroup} setNested={setNested} productsEnabled={productsEnabled} onOpenAssets={onOpenAssets} />,
    categories: <CategoriesInspector form={form} setGroup={setGroup} setNested={setNested} productsEnabled={productsEnabled} />,
    about: <AboutInspector form={form} setGroup={setGroup} productsEnabled={productsEnabled} simpleMode={simpleMode} onOpenAssets={onOpenAssets} />,
    highlights: <HighlightsInspector form={form} setGroup={setGroup} addHighlight={addHighlight} updateHighlight={updateHighlight} removeHighlight={removeHighlight} simpleMode={simpleMode} onOpenAssets={onOpenAssets} />,
    contact: <ContactInspector form={form} setGroup={setGroup} setNested={setNested} simpleMode={simpleMode} onOpenContactSettings={onOpenContactSettings} />,
    footer: <FooterInspector form={form} setGroup={setGroup} simpleMode={simpleMode} />,
    floatingWhatsapp: <FloatingWhatsappInspector form={form} setGroup={setGroup} />,
    identity: <IdentityInspector form={form} setRoot={setRoot} setGroup={setGroup} onOpenAssets={onOpenAssets} simpleMode={simpleMode} />,
    seo: <SeoInspector form={form} setGroup={setGroup} />,
  }[activeSection];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(25,36,47,.10)",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
        {content || (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900 }}>Seleccioná una sección de la landing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sus controles aparecerán acá.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
